import api from '@/utils/axios';

/** Maps UI filter values to employees/get `is_active` (1, 0, or 'all'). */
export function normalizeEmployeeActiveFilter(value) {
  if (value === 'all' || value === null || value === undefined || value === '') return 'all';
  if (value === 'active' || value === true || value === 1 || value === '1') return 1;
  if (value === 'inactive' || value === false || value === 0 || value === '0') return 0;
  return 'all';
}

export const fetchGrades = async () => {
  const token = localStorage.getItem('srToken');
  const response = await api.post('grades/get', { token });
  if (!response.data?.success) {
    throw new Error(
      response.data?.error?.message || response.data?.message || 'Failed to fetch grades'
    );
  }
  const data = response.data.data;
  if (Array.isArray(data)) return data;
  return data?.grades || [];
};

export const fetchRolesForEmployee = async ({ departmentId = null } = {}) => {
  const token = localStorage.getItem('srToken');
  const response = await api.post('roles/get', {
    token,
    department_id: departmentId,
    purpose: 'system'
  });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || response.data?.message || 'Failed to fetch roles');
  }
  return response.data.data || [];
};

export const changeEmployeePassword = async ({ employee_id, password }) => {
  const response = await api.post('employees/manage', {
    action: 'change_password',
    employee_id,
    password
  });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to change password');
  }
  return response.data.data;
};

export const fetchEmployees = async ({
  page = 1,
  limit = 10,
  search = '',
  department_id = null,
  position_id = null,
  team_id = null,
  team_unassigned = null,
  sub_department_id = null,
  sub_department_unassigned = null,
  grade_id = null,
  work_location = null,
  location_id = null,
  terminal_id = null,
  country = null,
  company_name = null,
  employment_type = null,
  employment_status = null,
  roster_mode = null,
  completeness = null,
  away_only = null,
  hired_this_month_only = null,
  end_date_from = null,
  end_date_to = null,
  with_summary = false,
  summary_scope = null,
  is_active = 'all',
  view = null,
  user_id = null,
  sortField = 'id',
  sortOrder = 'DESC'
} = {}) => {
  const response = await api.post('employees/get', {
    page,
    limit,
    search,
    department_id,
    position_id,
    ...(team_id != null ? { team_id } : {}),
    ...(team_unassigned ? { team_unassigned: 1 } : {}),
    ...(sub_department_id != null ? { sub_department_id } : {}),
    ...(sub_department_unassigned ? { sub_department_unassigned: 1 } : {}),
    grade_id,
    work_location,
    location_id,
    ...(terminal_id != null ? { terminal_id } : {}),
    ...(country ? { country } : {}),
    ...(company_name ? { company_name } : {}),
    employment_type,
    employment_status,
    ...(roster_mode ? { roster_mode } : {}),
    completeness,
    ...(away_only ? { away_only: 1 } : {}),
    ...(hired_this_month_only ? { hired_this_month_only: 1 } : {}),
    ...(end_date_from ? { end_date_from } : {}),
    ...(end_date_to ? { end_date_to } : {}),
    ...(with_summary ? { with_summary: 1 } : {}),
    ...(summary_scope ? { summary_scope } : {}),
    is_active: normalizeEmployeeActiveFilter(is_active),
    ...(view ? { view } : {}),
    ...(user_id != null ? { user_id } : {}),
    sortField,
    sortOrder
  });

  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to fetch employees');
  }

  const data = response.data.data || {};
  return {
    employees: Array.isArray(data.employees) ? data.employees : [],
    total: Number(data.total || 0),
    page: Number(data.page || page),
    limit: Number(data.limit || limit),
    summary: data.summary || null,
    can_view_sensitive: data.can_view_sensitive === true
  };
};

/**
 * Canonical payload for the dedicated employee editor.
 *
 * Unlike `employees/get`, this endpoint supports the scoped self-edit case and
 * returns server-resolved capabilities for the concrete target employee.
 */
export const fetchEmployeeEditor = async ({ user_id }) => {
  const response = await api.post('employees/editor/get', { user_id });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to load employee editor');
  }

  const data = response.data.data || {};
  return {
    account: data.account || null,
    hr_profile: data.hr_profile || null,
    capabilities: data.capabilities && typeof data.capabilities === 'object'
      ? data.capabilities
      : {},
    editable_fields: Array.isArray(data.editable_fields) ? data.editable_fields : []
  };
};

export const createEmployee = async (payload) => {
  const response = await api.post('employees/manage', {
    action: 'create',
    ...payload
  });
  if (!response.data?.success) {
    const err = new Error(response.data?.error?.message || 'Failed to create employee');
    err.code = response.data?.error?.code;
    err.response = { status: 422, data: response.data };
    throw err;
  }
  return response.data.data;
};

export class EmployeeAccountConflictError extends Error {
  constructor(message, freshAccount) {
    super(message || 'Employee account was modified by someone else.');
    this.name = 'EmployeeAccountConflictError';
    this.conflict = true;
    this.conflictKind = 'account';
    this.freshAccount = freshAccount || null;
  }
}

export const updateEmployee = async (payload) => {
  try {
    const response = await api.post('employees/manage', {
      action: 'update',
      ...payload
    });
    if (!response.data?.success) {
      throw new Error(response.data?.error?.message || 'Failed to update employee');
    }
    return response.data.data;
  } catch (err) {
    if (err?.response?.status === 409 && err?.response?.data?.error?.code === 'CONFLICT') {
      throw new EmployeeAccountConflictError(
        err.response.data?.error?.message,
        err.response.data?.data?.account
      );
    }
    throw err;
  }
};

export const updateEmployeeAccessForTesting = async ({ employee_id, department_id, role_id, grade_id }) => {
  const response = await api.post('employees/manage', {
    action: 'access_switcher_update',
    employee_id,
    department_id,
    role_id: role_id ? [role_id] : [],
    grade_id
  });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to update employee access');
  }
  return response.data.data;
};

export const fetchAccessSwitcherOptions = async () => {
  const response = await api.post('employees/manage', {
    action: 'access_switcher_options'
  });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to fetch access switcher options');
  }
  return response.data.data || {};
};

export const updateEmployeeStatus = async ({ employee_id, is_active }) => {
  const response = await api.post('employees/manage', {
    action: 'update_status',
    employee_id,
    is_active: is_active ? 1 : 0
  });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to update employee status');
  }
  return response.data.data;
};

export const deleteEmployee = async ({ employee_id }) => {
  const response = await api.post('employees/manage', {
    action: 'delete',
    employee_id
  });
  if (!response.data?.success) {
    const err = response.data?.error;
    throw new Error(err?.message || 'Failed to move employee to recycle bin');
  }
  return response.data.data;
};

export const bulkDeleteEmployees = async ({ employee_ids }) => {
  const response = await api.post('employees/manage', {
    action: 'bulk_delete',
    employee_ids
  });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to move employees to recycle bin');
  }
  const data = response.data.data || {};
  return {
    deleted: Array.isArray(data.deleted) ? data.deleted : [],
    skipped: Array.isArray(data.skipped) ? data.skipped : [],
  };
};

export const fetchRecycleBin = async ({ page = 1, limit = 25, search = '', sortField = 'deleted_at', sortOrder = 'DESC' } = {}) => {
  const response = await api.post('employees/recycle/get', { page, limit, search, sortField, sortOrder });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to load recycle bin');
  }
  const data = response.data.data || {};
  return {
    items: Array.isArray(data.items) ? data.items : [],
    total: Number(data.total || 0),
    page: Number(data.page || page),
    limit: Number(data.limit || limit),
  };
};

export const restoreFromRecycle = async ({ user_id }) => {
  const response = await api.post('employees/recycle/manage', {
    action: 'restore',
    user_id
  });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to restore employee');
  }
  return response.data.data;
};

export const purgeFromRecycle = async ({ user_id }) => {
  const response = await api.post('employees/recycle/manage', {
    action: 'purge',
    user_id
  });
  if (!response.data?.success) {
    const err = response.data?.error;
    const details = Array.isArray(err?.details) && err.details.length
      ? ` (${err.details.map((d) => `${d.table}.${d.column}: ${d.count}`).join(', ')})`
      : '';
    throw new Error((err?.message || 'Failed to purge employee') + details);
  }
  return response.data.data;
};

export const uploadEmployeeAvatar = async ({ employee_id, file }) => {
  const formData = new FormData();
  formData.append('employee_id', String(employee_id));
  formData.append('avatar', file);

  const token = localStorage.getItem('srToken');
  if (token) {
    formData.append('token', token);
  }

  const response = await api.post('employees/avatar/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });

  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to upload avatar');
  }
  return response.data.data;
};

export const deleteEmployeeAvatar = async ({ employee_id }) => {
  const response = await api.post('employees/avatar/delete', {
    employee_id
  });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to delete avatar');
  }
  return response.data.data;
};

/** Thrown on optimistic-lock conflict (409) from profile/manage. Carries the fresh profile. */
export class HrProfileConflictError extends Error {
  constructor(message, freshProfile) {
    super(message || 'Profile was modified by someone else. Reload and try again.');
    this.name = 'HrProfileConflictError';
    this.conflict = true;
    this.freshProfile = freshProfile || null;
  }
}

/** Single employee row for directory / profile report (users.id). */
export const fetchDirectoryEmployeeByUserId = async (user_id) => {
  const response = await fetchEmployees({
    page: 1,
    limit: 1,
    view: 'directory',
    roster_mode: 'all',
    is_active: 'all',
    user_id
  });
  return response.employees[0] || null;
};

/** Presence fields for one employee (users.id) from employees/get. */
export const fetchEmployeePresenceByUserId = async (user_id) => {
  const response = await fetchEmployees({
    page: 1,
    limit: 1,
    user_id
  });
  return response.employees[0] || null;
};

/** Load one HR profile (employees entity) by users.id. Returns the profile object. */
export const fetchEmployeeHrProfile = async ({ user_id }) => {
  const response = await api.post('employees/profile/get', { user_id });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to load HR profile');
  }
  return response.data.data?.profile || null;
};

/**
 * Update HR profile fields (employees entity). `updated_at` is required for optimistic locking.
 * On 409 throws HrProfileConflictError carrying the fresh server profile.
 */
export const manageEmployeeHrProfile = async ({ user_id, updated_at, ...fields }) => {
  try {
    const response = await api.post('employees/profile/manage', { user_id, updated_at, ...fields });
    if (!response.data?.success) {
      throw new Error(response.data?.error?.message || 'Failed to save HR profile');
    }
    return response.data.data?.profile || null;
  } catch (err) {
    const resp = err?.response;
    if (resp?.status === 409) {
      const fresh = resp.data?.data?.profile || null;
      throw new HrProfileConflictError(resp.data?.error?.message, fresh);
    }
    throw err;
  }
};

/**
 * Reference data for the profile form (departments, positions, grades, locations,
 * terminals, sub_departments, teams, managers). Pass `user_id` on edit so the current
 * employee is excluded from the managers list (no self-manager).
 */
export const fetchEmployeeProfileOptions = async ({ user_id, include_inactive } = {}) => {
  const body = {};
  if (user_id != null) body.user_id = user_id;
  if (include_inactive) body.include_inactive = 1;
  const response = await api.post('employees/profile/options/get', body);
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to load profile options');
  }
  const data = response.data.data || {};
  return {
    departments: Array.isArray(data.departments) ? data.departments : [],
    positions: Array.isArray(data.positions) ? data.positions : [],
    grades: Array.isArray(data.grades) ? data.grades : [],
    locations: Array.isArray(data.locations) ? data.locations : [],
    terminals: Array.isArray(data.terminals) ? data.terminals : [],
    sub_departments: Array.isArray(data.sub_departments) ? data.sub_departments : [],
    teams: Array.isArray(data.teams) ? data.teams : [],
    managers: Array.isArray(data.managers) ? data.managers : []
  };
};

/**
 * Load field-level audit log entries for one employee (newest first).
 * Access: super_admin, `audit.view_all`, or `audit.view_employee` + `employees_hr.view`.
 */
export const fetchEmployeeAudit = async ({ user_id, employee_id, limit = 50, offset = 0 } = {}) => {
  const body = { limit, offset };
  if (employee_id != null) body.employee_id = employee_id;
  else if (user_id != null) body.user_id = user_id;
  const response = await api.post('audit/employee/get', body);
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to load audit log');
  }
  const data = response.data.data || {};
  return {
    entries: Array.isArray(data.entries) ? data.entries : [],
    total: Number(data.total || 0),
    limit: Number(data.limit || limit),
    offset: Number(data.offset || offset)
  };
};

export const fetchEmployeeProfileMessages = async ({ employee_id }) => {
  const response = await api.post('employees/profile_messages/get', { employee_id });
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to load profile notes');
  }
  const data = response.data.data || {};
  return {
    messages: Array.isArray(data.messages) ? data.messages : []
  };
};

export const manageEmployeeProfileMessage = async (payload) => {
  const response = await api.post('employees/profile_messages/manage', payload);
  if (!response.data?.success) {
    throw new Error(response.data?.error?.message || 'Failed to save profile note');
  }
  return response.data.data || {};
};
